'use client';

import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Icon from '@/components/ui/AppIcon';
import GlobalSearch from './GlobalSearch';
import AlarmNotificationBell from './AlarmNotificationBell';

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem('netmanager-theme');
    if (saved === 'light') {
      setDarkMode(false);
      document.documentElement.classList.add('light-mode');
    }
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    if (next) {
      document.documentElement.classList.remove('light-mode');
      localStorage.setItem('netmanager-theme', 'dark');
    } else {
      document.documentElement.classList.add('light-mode');
      localStorage.setItem('netmanager-theme', 'light');
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar — desktop */}
      <div className="hidden lg:flex flex-shrink-0 h-full">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </div>

      {/* Sidebar — mobile drawer */}
      <div className={`fixed inset-y-0 left-0 z-50 lg:hidden transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar collapsed={false} onToggle={() => setMobileOpen(false)} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center h-14 px-4 border-b border-border bg-surface flex-shrink-0 gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-surface-elevated transition-colors"
          >
            <Icon name="Bars3Icon" size={20} className="text-secondary-color" />
          </button>

          {/* Global Search */}
          <div className="flex-1 max-w-sm hidden sm:block">
            <GlobalSearch />
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Alarm Notification Bell */}
            <AlarmNotificationBell />
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={darkMode ? 'Passa alla modalità chiara' : 'Passa alla modalità scura'}
              className="p-2 rounded-lg hover:bg-surface-elevated transition-colors"
            >
              <Icon name={darkMode ? 'SunIcon' : 'MoonIcon'} size={18} className="text-muted-color hover:text-white transition-colors" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}